import React, { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Logo from "./Logo";

const NAV = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About" },
    { to: "/services", label: "Services" },
    { to: "/how-we-work", label: "How We Work" },
    { to: "/contact", label: "Contact" },
];

export const Header = () => {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
                scrolled
                    ? "bg-[#05070A]/85 backdrop-blur-md border-b border-white/[0.06]"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-10 h-28 flex items-center justify-between">
                <Link to="/" data-testid="brand-logo-link">
                    <Logo />
                </Link>

                <nav className="hidden md:flex items-center gap-10" aria-label="Primary">
                    {NAV.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.to === "/"}
                            data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={({ isActive }) =>
                                `hover-line text-[13px] tracking-[0.22em] uppercase font-medium transition-colors ${
                                    isActive ? "text-white" : "text-white/55 hover:text-white"
                                }`
                            }
                        >
                            {item.label}
                        </NavLink>
                    ))}
                </nav>

                <Link
                    to="/contact"
                    data-testid="header-cta-contact"
                    className="hidden md:inline-flex items-center border border-white/25 text-white px-5 py-3 text-[12px] tracking-[0.24em] uppercase font-medium hover:bg-white hover:text-[#05070A] transition-colors duration-300"
                >
                    Book a conversation
                </Link>

                <button
                    type="button"
                    onClick={() => setOpen((s) => !s)}
                    aria-label="Toggle menu"
                    data-testid="mobile-menu-toggle"
                    className="md:hidden text-white p-2 -mr-2"
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

            {open && (
                <div
                    data-testid="mobile-menu"
                    className="md:hidden bg-[#05070A] border-t border-white/[0.06]"
                >
                    <div className="px-6 py-8 flex flex-col gap-6">
                        {NAV.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.to === "/"}
                                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className={({ isActive }) =>
                                    `text-[13px] tracking-[0.22em] uppercase font-medium ${
                                        isActive ? "text-white" : "text-white/60"
                                    }`
                                }
                            >
                                {item.label}
                            </NavLink>
                        ))}
                        <Link
                            to="/contact"
                            data-testid="mobile-cta-contact"
                            className="mt-4 inline-flex items-center justify-center border border-white/25 text-white px-5 py-4 text-[12px] tracking-[0.24em] uppercase"
                        >
                            Book a conversation
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
};

export default Header;
